import React, { useEffect } from "react";
import {
  useLazyGetFirmByIDQuery,
  useLazyGetDealerRolesFirmByIDQuery,
  usePreviewFirmAutoReportMutation,
  useSendFirmAutoReportMutation,
  useSaveFirmAutoReportConfigMutation,
  useDealerPreviewFirmAutoReportMutation,
  useDealerSendFirmAutoReportMutation,
  useDealerSaveFirmAutoReportConfigMutation,
} from "../../../store";
import useAuth from "../../../Hooks/useAuth";
import AutoReportDrawer from "./AutoReportDrawer";

/**
 * Müşteri / bayi (#209) firmasına giden otomatik rapor e-postası (Firma → Bilgiler →
 * Rapor Ayarları). Merkez ve bayi kendi uçlarını kullanır.
 */
const FirmAutoReportDrawer = ({ open, onClose, firm, token, onSuccess }) => {
  const { auth } = useAuth();
  const dealer = auth?.role === "dealer";

  const [getFirmStorage, firmStorageState] = useLazyGetFirmByIDQuery();
  const [getFirmDealer, firmDealerState] = useLazyGetDealerRolesFirmByIDQuery();
  const [saveStorage, saveStorageState] = useSaveFirmAutoReportConfigMutation();
  const [saveDealer, saveDealerState] = useDealerSaveFirmAutoReportConfigMutation();
  const [previewStorage, previewStorageState] = usePreviewFirmAutoReportMutation();
  const [sendStorage, sendStorageState] = useSendFirmAutoReportMutation();
  const [previewDealer, previewDealerState] = useDealerPreviewFirmAutoReportMutation();
  const [sendDealer, sendDealerState] = useDealerSendFirmAutoReportMutation();

  const getFirm = dealer ? getFirmDealer : getFirmStorage;
  const firmState = dealer ? firmDealerState : firmStorageState;
  const saveMutation = dealer ? saveDealer : saveStorage;
  const previewMutation = dealer ? previewDealer : previewStorage;
  const sendMutation = dealer ? sendDealer : sendStorage;

  const fullFirm = firmState?.data?.data?.firm || firmState?.data?.firm || firmState?.data;
  const loadedFirm = fullFirm?._id && fullFirm._id === firm?._id ? fullFirm : null;

  useEffect(() => {
    if (open && firm?._id && token) getFirm({ id: firm._id, token });
  }, [open, firm?._id, token, getFirm]);

  let status = "ready";
  let message = null;
  if (firmState.isFetching || (!loadedFirm && !firmState.isError)) status = "loading";
  else if (!loadedFirm) {
    status = "error";
    message =
      firmState.error?.status === 403
        ? "Bu firmanın rapor ayarlarını görmek için yetkiniz yok."
        : "Firma bilgisi yüklenemedi.";
  } else if (!["customer", "dealer"].includes(loadedFirm.firmType)) {
    status = "unavailable";
    message = "Otomatik rapor yalnızca müşteri ve bayi firmaları için ayarlanabilir.";
  }

  const handleSave = async (config) => {
    await saveMutation({ id: loadedFirm._id, autoReportMailConfig: config, token }).unwrap();
    onSuccess?.();
  };

  const handlePreview = async (frequency, cfg) => {
    const resp = await previewMutation({
      id: loadedFirm._id,
      frequency,
      coverage: cfg?.coverage,
      sections: cfg?.sections,
      token,
    }).unwrap();
    return resp?.data;
  };

  const handleSend = (frequency, cfg) =>
    sendMutation({
      id: loadedFirm._id,
      frequency,
      coverage: cfg?.coverage,
      sections: cfg?.sections,
      token,
    }).unwrap();

  return (
    <AutoReportDrawer
      open={open}
      onClose={onClose}
      title="Rapor Ayarları"
      subtitle={firm?.firmName}
      status={status}
      message={message}
      recipient={
        <>
          Açık olan raporlar seçilen zamanda PDF olarak{" "}
          {loadedFirm?.email ? (
            <><span className="font-SemiBold text-gray-800">{loadedFirm.email}</span> adresine</>
          ) : (
            "firmanın e-posta adresine"
          )}{" "}
          gönderilir.
        </>
      }
      savedConfig={loadedFirm?.autoReportMailConfig}
      onSave={handleSave}
      onPreview={handlePreview}
      onSend={handleSend}
      isSaving={dealer ? saveDealerState.isLoading : saveStorageState.isLoading}
      isPreviewing={dealer ? previewDealerState.isLoading : previewStorageState.isLoading}
      isSending={dealer ? sendDealerState.isLoading : sendStorageState.isLoading}
    />
  );
};

export default FirmAutoReportDrawer;
